// File: oneportal-shell/src/api/shopifyConfig.ts
// Shopify Storefront API client configuration

interface ShopifyConfig {
  storefrontUrl: string;
  storefrontToken: string;
  adminUrl: string;
  adminToken: string;
}

/**
 * Get Shopify API URLs and tokens from environment variables
 */
function getShopifyConfig(): ShopifyConfig {
  const storefrontUrl = import.meta.env.VITE_SHOPIFY_STOREFRONT_URL;
  const storefrontToken = import.meta.env.VITE_SHOPIFY_STOREFRONT_TOKEN;
  const adminUrl = import.meta.env.VITE_SHOPIFY_ADMIN_URL;
  const adminToken = import.meta.env.VITE_SHOPIFY_ADMIN_TOKEN || '';

  if (!storefrontUrl || !storefrontToken) {
    console.error('Missing required Shopify Storefront API configuration');
    throw new Error(
      'VITE_SHOPIFY_STOREFRONT_URL and VITE_SHOPIFY_STOREFRONT_TOKEN must be set in environment variables'
    );
  }

  return {
    storefrontUrl,
    storefrontToken,
    adminUrl,
    adminToken,
  };
}

/**
 * Create authenticated fetch wrapper for Shopify Storefront API
 */
export function createShopifyStorefrontFetch(): typeof fetch {
  return async (url: RequestInfo | URL, init?: RequestInit) => {
    const config = getShopifyConfig();
    const enhancedInit: RequestInit = { ...init };
    const headers = new Headers(enhancedInit.headers);

    // Add Shopify Storefront API authentication
    headers.set('X-Shopify-Storefront-Access-Token', config.storefrontToken);
    headers.set('Content-Type', 'application/json');
    headers.set('Accept', 'application/json');

    enhancedInit.headers = headers;

    // Use configured Storefront URL
    const fullUrl = typeof url === 'string' ? config.storefrontUrl : url;

    try {
      const response = await fetch(fullUrl, enhancedInit);
      return response;
    } catch (error) {
      console.error('Shopify Storefront API fetch error:', error);
      throw error;
    }
  };
}

/**
 * Create authenticated fetch wrapper for Shopify Admin API (placeholder)
 * NOTE: Admin API token should be kept server-side for security
 */
export function createShopifyAdminFetch(): typeof fetch {
  return async (url: RequestInfo | URL, init?: RequestInit) => {
    const config = getShopifyConfig();

    if (!config.adminToken) {
      console.warn(
        'Shopify Admin API token not configured. This is expected for client-side apps.'
      );
      throw new Error('Shopify Admin API not available in client-side context');
    }

    const enhancedInit: RequestInit = { ...init };
    const headers = new Headers(enhancedInit.headers);

    // Add Shopify Admin API authentication
    headers.set('X-Shopify-Access-Token', config.adminToken);
    headers.set('Content-Type', 'application/json');
    headers.set('Accept', 'application/json');

    enhancedInit.headers = headers;

    // Use configured Admin URL
    const fullUrl = typeof url === 'string' ? config.adminUrl : url;

    try {
      const response = await fetch(fullUrl, enhancedInit);
      return response;
    } catch (error) {
      console.error('Shopify Admin API fetch error:', error);
      throw error;
    }
  };
}

/**
 * Shopify GraphQL client for direct API calls
 */
export const shopifyStorefrontClient = {
  /**
   * Execute a GraphQL query against Shopify Storefront API
   */
  async query<T = any>(query: string, variables?: Record<string, any>): Promise<T> {
    const fetchFn = createShopifyStorefrontFetch();
    const config = getShopifyConfig();

    const response = await fetchFn(config.storefrontUrl, {
      method: 'POST',
      body: JSON.stringify({
        query,
        variables: variables || {},
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Shopify API error: ${response.statusText} - ${errorText}`);
    }

    const result = await response.json();

    if (result.errors) {
      console.error('Shopify GraphQL errors:', result.errors);
      throw new Error(`Shopify GraphQL error: ${JSON.stringify(result.errors)}`);
    }

    return result.data as T;
  },
};

/**
 * Shopify Admin API client (placeholder for future server-side integration)
 */
export const shopifyAdminClient = {
  /**
   * Execute a GraphQL query against Shopify Admin API
   * NOTE: This should only be used from server-side code for security
   */
  async query<T = any>(query: string, variables?: Record<string, any>): Promise<T> {
    const fetchFn = createShopifyAdminFetch();
    const config = getShopifyConfig();

    const response = await fetchFn(config.adminUrl, {
      method: 'POST',
      body: JSON.stringify({
        query,
        variables: variables || {},
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Shopify Admin API error: ${response.statusText} - ${errorText}`);
    }

    const result = await response.json();

    if (result.errors) {
      console.error('Shopify Admin GraphQL errors:', result.errors);
      throw new Error(`Shopify Admin GraphQL error: ${JSON.stringify(result.errors)}`);
    }

    return result.data as T;
  },
};
